import { GsFormField, GsFormIfArg } from '../../../components';
import { PaginationStyle } from '../../../controllers/gs-pagination/models/pagination-style';

/**
 * The schema fields for items per row configuration.
 */
export const ITEMS_PER_ROW_SCHEMA_FIELDS: GsFormField<never>[] = [
  // Items per row for desktop
  {
    formControlName: 'itemsRowDesktop',
    label: 'schema.items.itemsRowDesktop',
    type: 'number',
    default: 4,
    min: 1,
    groupingName: 'schema.items.name'
  },
  // Items per row for tablet
  {
    formControlName: 'itemsRowTablet',
    label: 'schema.items.itemsRowTablet',
    type: 'number',
    default: 2,
    min: 1,
    groupingName: 'schema.items.name'
  },
  // Items per row for mobile
  {
    formControlName: 'itemsRowMobile',
    label: 'schema.items.itemsRowMobile',
    type: 'number',
    default: 1,
    min: 1,
    max: 2,
    groupingName: 'schema.items.name'
  }
];

/**
 * Generates paginated schema fields with optional mode-specific pagination types.
 * @param includeInfiniteScroll - Whether to include the INFINITE_SCROLL option (only for GRID mode)
 * @param modeIfArgs - Optional mode-specific if arguments for conditional display
 * @returns Array of form fields for pagination configuration
 */
export function generatePaginatedSchemaFields(includeInfiniteScroll: boolean = false, modeIfArgs?: GsFormIfArg[]): GsFormField<never>[] {
  const paginationOptions: Array<{ text: string; value: PaginationStyle }> = [
    { text: 'schema.paginated.type.loadMore', value: PaginationStyle.LOAD_MORE },
    { text: 'schema.paginated.type.simple', value: PaginationStyle.SIMPLE },
    { text: 'schema.paginated.type.default', value: PaginationStyle.DEFAULT }
  ];

  // Add INFINITE_SCROLL option only for GRID mode
  if (includeInfiniteScroll) {
    paginationOptions.push({ text: 'schema.paginated.type.infiniteScroll', value: PaginationStyle.INFINITE_SCROLL });
  }

  return [
    // Pagination enabled?
    {
      formControlName: 'withPagination',
      groupingName: 'schema.paginated.name',
      label: 'schema.paginated.showPagination',
      type: 'checkbox',
      default: false
    },
    // Pagination type / If there should be a load more button or simple pagination
    {
      formControlName: 'paginationType',
      label: 'schema.paginated.paginationType',
      groupingName: 'schema.paginated.name',
      type: 'select',
      valueType: 'number',
      default: PaginationStyle.LOAD_MORE,
      options: paginationOptions,
      if: {
        chaining: 'and',
        args: [
          {
            formControlName: 'withPagination',
            value: true
          },
          ...(modeIfArgs ?? [])
        ]
      }
    },
    // Amount of items per page
    {
      formControlName: 'pageSize',
      label: 'schema.items.pageSize',
      type: 'number',
      default: 12,
      min: 1,
      groupingName: 'schema.items.name'
    },
    ...ITEMS_PER_ROW_SCHEMA_FIELDS
  ];
}

/**
 * The schema fields for paginated components (default without INFINITE_SCROLL).
 */
export const PAGINATED_SCHEMA_FIELDS: GsFormField<never>[] = generatePaginatedSchemaFields();
